import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { StaticDatePicker, StaticDatePickerProps } from '@mui/x-date-pickers/StaticDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import TextInput from './TextInput'

import { adapterLocaleMap } from '@/utils/locale'

dayjs.extend(localizedFormat)

export type StaticDatePickerInputProps = Omit<StaticDatePickerProps<Dayjs, Dayjs>, 'renderInput'> & {
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * If true, the input will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  /**
   * Callback fired when the input is blur.
   *
   * @param {object} event The event source of the callback.
   */
  /**
   * The value of the picker
   */
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  value: Dayjs | null
}

export function StaticDatePickerInput(props: StaticDatePickerInputProps) {
  const { error, fullWidth, helperText, name, onBlur, ...rest } = props
  const { i18n } = useTranslation()

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocaleMap[i18n.language.split('-')[0]]}>
      <StaticDatePicker
        {...rest}
        inputFormat="DD/MM/YYYY"
        openTo="day"
        orientation="landscape"
        OpenPickerButtonProps={{
          size: 'small',
        }}
        renderInput={(params) => (
          <TextInput
            {...params}
            inputProps={{
              ...params.inputProps,
              'data-testid': 'datetime-input',
            }}
            fullWidth={fullWidth}
            helperText={helperText}
            name={name}
            onBlur={onBlur}
            error={params.error || error}
          />
        )}
      />
    </LocalizationProvider>
  )
}

export default StaticDatePickerInput
